import { VehiclesQuery } from 'src/generated'
import type { PastelName } from 'src/theme/palette'

// ----------------------------------------------------------------------
// What a car on the map is doing, and what that looks like.
//
// One table, two consumers: the marker draws a row and the legend explains
// the same row. They used to hold two lists that agreed on the words and not
// on the colour: the marker painted the categorical `bg` and the legend
// painted the categorical `text`, so the key showed four dark inks beside four
// near-white pins and none of the four pairs matched. A legend that is not the
// colour on the map is worse than no legend: it is a key that has to be
// distrusted before it can be used.
//
// **The hue is `vivid`, and that is what makes the colours tell apart.** The
// pastel pair is the chip pair, deliberately quiet because a console surface
// is quiet. On a basemap the console does not control, quiet is invisible: the
// four marker fills measured 1.00:1 to 1.14:1 from each other and 1.14:1 to
// 1.30:1 against the pill they sat in, which is four white discs. `vivid` is
// the same hue at full strength (the colour each state has always been, not a
// new one), and it lands the fills 2.28:1 to 4.99:1 against the pill.
//
// Colour is still never the only signal: each row carries its own glyph, the
// state is in the marker's accessible name, and the legend names it in words.
// ----------------------------------------------------------------------

/** The states a marker can take. */
export type MarkerState = 'available' | 'booking' | 'freestyle' | 'disabled'

export interface MarkerStateEntry {
  state: MarkerState
  /** What the legend calls it. The marker's own label uses `state`. */
  label: string
  /**
   * The categorical hue, resolved through `theme.palette.vivid`. Named rather
   * than resolved here so the row is a plain value and the colour is still the
   * theme's in both schemes.
   */
  tint: PastelName
  icon: string
}

/**
 * Every state, in the order the legend reads them: the healthy one first, then
 * the two kinds of in-use, then the one that is out of service. This is not
 * the order they are *decided* in: that is `markerStateOf`, which asks about
 * a rental first because a rented car is in a rental whatever else is true of
 * it.
 */
export const MARKER_STATES: readonly MarkerStateEntry[] = [
  {
    state: 'available',
    label: 'Available',
    tint: 'mint',
    icon: 'lucide:circle-dot',
  },
  {
    state: 'booking',
    label: 'In rental',
    tint: 'lavender',
    icon: 'lucide:calendar',
  },
  {
    state: 'freestyle',
    label: 'Freestyle',
    tint: 'peach',
    icon: 'tabler:steering-wheel',
  },
  {
    state: 'disabled',
    label: 'Disabled',
    tint: 'grey',
    icon: 'lucide:circle-minus',
  },
]

const BY_STATE = Object.fromEntries(
  MARKER_STATES.map((entry) => [entry.state, entry]),
) as Record<MarkerState, MarkerStateEntry>

/** The row a state draws with. */
export const markerState = (state: MarkerState): MarkerStateEntry =>
  BY_STATE[state]

type MarkerVehicle = Pick<
  VehiclesQuery['vehicles']['vehicles'][0],
  'latestStatus' | 'rental'
> &
  Partial<Pick<VehiclesQuery['vehicles']['vehicles'][0], 'disabled'>>

/**
 * What a vehicle is doing, in the order it is decided: a car in a rental is in
 * a rental whatever its disabled flag says, and disabled is a thing the fleet
 * did rather than a thing the car is doing, so it comes before available.
 *
 * Disabled is `vehicles.disabled`, off hire, and not the freestyle switch.
 * The pin read the switch, which only takes a car off the freestyle map, so a
 * car anybody could book stood grey on the map beside a callout that, since
 * 2026-09-27, says it is free. A record without the field falls back to the
 * switch, which is the reading every caller had before.
 */
export const markerStateOf = (vehicle: MarkerVehicle): MarkerState => {
  if (vehicle.rental?.rentalType) {
    return vehicle.rental.rentalType as MarkerState
  }
  if (vehicle.disabled ?? vehicle.latestStatus?.isDisabled) {
    return 'disabled'
  }
  return 'available'
}
